<template>
  <Modal :is-open="isOpen" @update:is-open="handleIsOpenUpdate" :onClose="handleClose" :modalWidth="560">
    <template #header>گزارش تخلف </template>
    <template #body>
      <TheForm ref="reportForm" @submit="handleSubmit">
        <div class="report-problem-modal gap-24 d-flex column">
          <div class="d-flex column gap-16">
            <div class="text-b14 color-black text-medium">مشخصات لینک پرداخت</div>
            <MerchantData v-if="personalLinkData && personalLinkData?.merchant" :merchant="personalLinkData?.merchant" :is-personal="true" />
            <ZarinLinkData v-if="linkData && linkData?.merchant" :zarinlink="linkData" />
          </div>
          <div class="d-flex column">
            <div class="d-flex column gap-16 h-auto">
              <BaseDropdown v-model="form.selectedType" :options="reportTypes" placeholder="مورد تخلف را انتخاب کنید" label="دلیل گزارش" class="mb-8" :rules="'required'" />
              <label class="text-b14 color-black text-medium">شرح گزارش</label>
              <BaseTextField v-model="form.description" name="description" type="textarea" placeholder="توضیحات (اختیاری)" />
            </div>
            <div class="d-flex column gap-16 checkbox">
              <BaseCheckbox v-model="form.contact" label="مایلم در صورت لزوم با من تماس گرفته شود." />
              <BaseTextField
                v-if="form.contact"
                v-model="form.mobile"
                name="contactInfo"
                type="tel"
                :rules="form.contact ? ['required', 'phone'] : []"
                placeholder="شماره موبایل (اجباری)"
              />
            </div>
          </div>
        </div>
      </TheForm>
    </template>
    <template #footer>
      <div class="d-flex w-full justify-end">
        <div class="d-flex gap-8 footer-container">
          <BaseButton type="outlined-dim" @click="handleClose"> انصراف </BaseButton>
          <BaseButton type="primary" :loading="loading" @click="submitForm"> ثبت گزارش </BaseButton>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, reactive, inject, type Ref, provide, nextTick, toRefs } from 'vue';
import { type ZarinLink, type PersonalLink, FraudReportEnumType, type FraudReportParam } from '@/types/api';
import { sendReportProblem } from '@/api/service/services';
import Modal from '@/components/base/modal/index.vue';
import BaseTextField from '@/components/base/text-field/index.vue';
import BaseButton from '@/components/base/button/index.vue';
import BaseDropdown from '@/components/base/dropdown/index.vue';
import MerchantData from '@/components/report-problem-modal/merchant-data.vue';
import BaseCheckbox from '@/components/base/checkbox/index.vue';
import TheForm from '@/components/the-form/index.vue';
import ZarinLinkData from '@/components/report-problem-modal/zarinlink-data.vue';
import { useToast } from '@/composabels/useToast';
import { parsePath, scrollToErrorField } from '@/utils';

const { showToast } = useToast();

const props = defineProps<{
  isOpen: boolean;
  onClose: () => void;
  openSuccessModal: () => void;
}>();
const emit = defineEmits(['update:isOpen']);
const { onClose, openSuccessModal } = toRefs(props);
const linkData = inject<Ref<ZarinLink | null>>('link-data');
const personalLinkData = inject<Ref<PersonalLink | null>>('personal-link-data');
const loading = ref<boolean>(false);
const reportForm = ref<{ validate: () => boolean; $el: HTMLElement } | null>(null);

const initialFormValues = {
  title: '',
  description: '',
  mobile: '',
  contact: true,
  selectedType: null,
};

const form = reactive({ ...initialFormValues });

const handleIsOpenUpdate = (value: boolean) => {
  emit('update:isOpen', value);
};

const reportTypes = [
  { label: 'فروش کالا یا ارائه خدمات غیرقانونی', value: FraudReportEnumType.ILLEGAL_GOODS_OR_SERVICES },
  { label: 'تخلف مالی (مثل پول‌شویی، فرار مالیاتی و ...)', value: FraudReportEnumType.FINANCIAL_FRAUD },
  { label: 'درخواست ورود یا ارسال اطلاعات حساس', value: FraudReportEnumType.SENSITIVE_INFORMATION_REQUEST },
];

const handleClose = () => {
  Object.assign(form, initialFormValues);
  onClose.value();
};

const handleReportProblem = async () => {
  const zarinlinkId = parsePath() as string;
  const zpId = personalLinkData?.value?.merchant.zp_id;

  const data: FraudReportParam = {
    linkType: linkData?.value ? 'zarinLink' : 'personalLink',
    zarin_id: (linkData?.value ? zarinlinkId : zpId) as string,
    type: form.selectedType as unknown as FraudReportEnumType,
    description: form.description,
    contact_cellphone: form.mobile,
  };
  const response = await sendReportProblem(data);
  if (response?.meta && response?.meta?.code === 200) {
    openSuccessModal.value();
    nextTick(() => {
      handleClose();
    });
  }
};

const handleSubmit = async () => {
  if (!reportForm.value) return;
  const isValid = reportForm?.value?.validate();
  if (!isValid) return;

  loading.value = true;
  try {
    await handleReportProblem();
  } catch (error) {
    showToast('خطایی رخ داده است', 'error');
  } finally {
    loading.value = false;
  }
};

const submitForm = () => {
  if (reportForm.value) {
    const formElement = reportForm.value.$el as HTMLFormElement;
    formElement?.dispatchEvent(new Event('submit', { cancelable: true }));
  }
  nextTick(() => {
    scrollToErrorField(document.querySelector('.has-error, .error') as HTMLElement);
  });
};
provide('form', form);
</script>

<style lang="scss" scoped>
@import url('./index.scss');
</style>
